import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { showToast } from "redux/slices/Toasts";
import { RootState } from "redux/store";
import useToggle from "hooks/useToggle";
import { StockPopup } from "./Popups/StocksPopup";
import { addAGGridSelectedStocks, cleanDispensePricingDetails, deleteDispensePricing, getDispensePricing, updateDispensePricing, updateDispensePricingField } from "redux/slices/DispensePricing";
import { CustomDropDown, Spinner, Title, CommonColumnProperties, CustomColumns, ImportWizard, Modal, FormatType, TableRef } from "components";
import { ProcessingState, ValidationState } from "components/ImportWizard/ImportWizardTypes";
import { getStores } from "redux/slices/Stores";
import { ParseDate } from "helpers/data/CommonImport";
import { runBatchWorkers } from "components/WebWorkers/ZWebWorker";
import { DispensePricingImportWorkerData, DispensePricingImportWorkerResponseData } from "./dispensePricingImportWorker";
import { DispensePricingUpdateQtyType } from "models/functionModels/dispensePricing/DispensePricingUpdateFunction";
import { InternalResponse, ServerResponse } from "models/common/responseObjects";
import { onlyUnique } from "helpers/Functions";
import { AgGridReact } from "ag-grid-react";
import { AgGridToolbar, AgGridToolbarRef } from "components/AgGrid/AgGridToolbar";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import EditablePriceCellRenderer from "components/AgGrid/CellRenderers/EditablePriceCellRenderer";
import EditablePercentCellRenderer from "components/AgGrid/CellRenderers/EditablePercentCellRenderer";
import MoneyCellEditor from "components/AgGrid/CellEditors/MoneyCellEditor";
import { getDispensePricingConfig } from "TableConfigs/DispensePricingConfig";
import { ProductsSpecialPricing, ProductsSpecialPricingExtended } from "models/storeModels/ProductsSpecialPricing";
import { ZIconType } from "helpers/utility/iconMap";
import { Z } from "models/common/components";
import { ZTooltip } from "components/ZTooltip";
import { ZIcon } from "components/ZIcon";

export interface ProductsSpecialPricingExtendedWithError extends ProductsSpecialPricingExtended {
	hasError?: { [key in keyof ProductsSpecialPricingExtended]?: boolean };
}

export function DispensePricingList() {
	const form = useRef(null);
	const dispatch = useAppDispatch();
	const { dispensePricingId } = useParams();
	const [isValidated, setValidated] = useToggle();
	const [showMenu, setShowMenu] = useState<boolean>(false);
	const stores = useAppSelector((state: RootState) => state.stores);
	const [importPercentage, setImportPercentage] = useState<number>(0);
	const [isNewDispensePricing, setIsNewDispensePricing] = useState<boolean>(true);
	const dispensePricing = useAppSelector((state: RootState) => state.dispensePricing);
	const currentStoreGroupId = useAppSelector((state: RootState) => state.global.storeGroupId);
	const [showWarning, setShowWarning] = useState<boolean>(false);
	const user = useAppSelector((state: RootState) => state.logedinUser);
	const dispensePricingGridRef = useRef<AgGridReact>(null);
	const toolbarRef = useRef<AgGridToolbarRef>(null);
	const [dispensePriceData, setDispensePriceData] = useState<ProductsSpecialPricingExtendedWithError[]>([]);
	const [showDelete, setShowDelete] = useState<boolean>(false);
	const [numberOfErrors, setNumberOfErrors] = useState<number>(0);
	
	useEffect(() => {
		if (dispensePricingId != null) {
			dispatch(getStores({ storeGroupIds: [currentStoreGroupId] }));
			setStoresDropdown();
			dispatch(getDispensePricing({stores: [parseInt(dispensePricingId)] })).then((resp)=> {	
				if(resp.stores[0]?.response?.data != null)		
					setDispensePriceData(getEditableManagedCatalogueStockView(resp.stores[0].response.data));
			});
			
			handleChange("storeId", parseInt(dispensePricingId));
			handleChange("stores", [parseInt(dispensePricingId)]);
			setIsNewDispensePricing(false);
		} else {
			dispatch(cleanDispensePricingDetails());
			setIsNewDispensePricing(true);
		}
	}, [dispensePricingId]);

	useEffect(() => {
		setStoresDropdown();		
	}, [stores.table]);

	function handleSubmit() {
		handleChange("loading", true);
		setShowWarning(false);
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			if (isNewDispensePricing) {
				// dispatch(createDispansePricing(dispensePricing)).then((res) => handleUpdate(res));
			} else {				
				const selectedStocks = dispensePricingGridRef.current?.api.getSelectedRows() as ProductsSpecialPricing[];				
				if (selectedStocks.length === 0) {
					//Its all stock then!
					dispensePricingGridRef.current?.api.forEachNode((node) => {
						selectedStocks.push(node.data);
					});
				}
				cleanGridDates(selectedStocks);
				dispatch(updateDispensePricing({
					clearAllExistingPricing: dispensePricing.clearAllExistingPricing,
					updates: selectedStocks, 
					packQtyType: DispensePricingUpdateQtyType.Tablet, 
					stores: dispensePricing.stores
				})).then((res) => handleUpdate(res));
			}
		} else {
			handleChange("loading", false);
		}
		setValidated();
	}

	function cleanGridDates(stocks : ProductsSpecialPricing[]) {
		stocks.forEach((stck, iter) => {
			if(stck.clubFixedPriceStart != null){
				stocks[iter].clubFixedPriceStart = new Date(stck.clubFixedPriceStart).toDateString();
			}
			if(stck.clubFixedPriceEnd != null){
				stocks[iter].clubFixedPriceEnd = new Date(stck.clubFixedPriceEnd).toDateString();
			}
			if(stck.privateFixedPriceStart != null){
				stocks[iter].privateFixedPriceStart = new Date(stck.privateFixedPriceStart).toDateString();
			}
			if(stck.privateFixedPriceEnd != null){
				stocks[iter].privateFixedPriceEnd = new Date(stck.privateFixedPriceEnd).toDateString();
			}
			if(stck.pbsFixedPriceStart != null){
				stocks[iter].pbsFixedPriceStart = new Date(stck.pbsFixedPriceStart).toDateString();
			}
			if(stck.pbsFixedPriceEnd != null){
				stocks[iter].pbsFixedPriceEnd = new Date(stck.pbsFixedPriceEnd).toDateString();
			}
		});
	}

	function reLoadTable(){
		if (dispensePricingId != null) {
			dispatch(getDispensePricing({stores: [parseInt(dispensePricingId)] }))
				.then((resp)=> {			
					setDispensePriceData(getEditableManagedCatalogueStockView(resp.stores[0].response.data));
				});
		}
	}

	function getEditableManagedCatalogueStockView(data: null | ProductsSpecialPricingExtended[]): ProductsSpecialPricingExtended[] {
		const tableData = data == null? dispensePricing.table: data;
		let newData: ProductsSpecialPricingExtendedWithError[] = [];
		tableData.forEach(da => {
			const manage:  ProductsSpecialPricingExtendedWithError = {
				name: da.name,
				productID: da.productID,
				privatePricingOption: da.privatePricingOption,
				privateFixedPrice: da.privateFixedPrice,
				privateSpecialMarkup: da.privateSpecialMarkup,
				privateSpecialFee: da.privateSpecialFee,
				privateFixedPricePacks: da.privateFixedPricePacks,
				privateFixedPriceStart: da.privateFixedPriceStart,
				privateFixedPriceEnd: da.privateFixedPriceEnd,
				markupIsPercentage: da.markupIsPercentage,
				privateFeeOption: da.privateFeeOption,
				pbsPricingOption: da.pbsPricingOption,
				pbsFixedPrice: da.pbsFixedPrice,
				pbsDiscount: da.pbsDiscount,
				discountIsPercentage: da.discountIsPercentage,
				usePrivateSpecialPricing: da.usePrivateSpecialPricing,
				usePBSSpecialPricing: da.usePBSSpecialPricing,				
				pbsFixedPricePacks: da.pbsFixedPricePacks,
				pbsFixedPriceStart: da.pbsFixedPriceStart,
				pbsFixedPriceEnd: da.pbsFixedPriceEnd,
				productsSpecialPricingID: da.productsSpecialPricingID,
				useClubPricing: da.useClubPricing,
				clubFixedPrice: da.clubFixedPrice,
				clubFixedPricePacks: da.clubFixedPricePacks,
				clubFixedPriceStart: da.clubFixedPriceStart,
				clubFixedPriceEnd: da.clubFixedPriceEnd,
				hasError: undefined,
			};
			newData.push(manage);
		}); 
		if(newData.length == 0 && dispensePricingGridRef.current != null && dispensePricingGridRef.current?.api != undefined)
			dispensePricingGridRef.current?.api.showNoRowsOverlay();
		return newData;
	}

	function handleUpdate(res: ServerResponse<InternalResponse<boolean>>) {
		var errors = res.stores.filter((i) => i.response.error !== null).map((i) => i.response.error)
		if (errors.length === 0) { 
			dispensePricingId != null && dispatch(getDispensePricing({ stores: [parseInt(dispensePricingId)] }));
		} else {
			dispatch(showToast({ value: errors }));
		}
		handleChange("loading", false);
	}

	function handleDelete() {
		const tableData = dispensePricing.table.filter((li)=>!dispensePricing.selectedStocks.includes(li.productID));	
		const deletedIds = dispensePricing.table.filter((li)=>dispensePricing.selectedStocks.includes(li.productID)).map((m)=> m.productsSpecialPricingID);
					//Manage deleted Ids
		if (deletedIds.length > 0) {
			dispatch(deleteDispensePricing({
				clearAllExistingPricing: false,
				deletedProductsSpecialPricingIds: deletedIds,
				stores: dispensePricing.stores
			}))
		}
		handleChange("table", tableData);
		setDispensePriceData(getEditableManagedCatalogueStockView(tableData));	
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateDispensePricingField({ key: option, value: optionValue }));
	}

	function setStoresDropdown() {
		let stockGroupsDropdown: any[] = [];
		stores.table.forEach((item: any) => {
			//@ts-ignore
			stockGroupsDropdown.push({ id: item.id, label: item.name, checked: dispensePricing.stores.includes(item.id) ? true : false });
		});
		handleChange("stockGroupsDropdown", stockGroupsDropdown);
	}

	function handleCheckedDropdown(key: number, label: string, value: any) {
		let storesGroupsDropdown: any[] = [];
		storesGroupsDropdown.push(...dispensePricing.stockGroupsDropdown);
		let index = storesGroupsDropdown.findIndex((item) => item.id === key);
		storesGroupsDropdown[index] = { id: key, label: label, checked: value };
		let selectedStockGroups: any[] = [];
		storesGroupsDropdown.forEach((item: any) => {
			if (item.checked) selectedStockGroups.push(item.id);
		});
		dispatch(updateDispensePricingField({ key: "stockGroupsDropdown", value: storesGroupsDropdown }));
		dispatch(updateDispensePricingField({ key: "stores", value: selectedStockGroups }));
	}

	//Section for the wizard

	function PriceValidation(value: any, type: string): { valid: boolean, error: string, newValue?: any } {

		//If empty, then we assume its fine
		if (value === undefined || value === null || value === 0) {
			return { valid: true, error: "", newValue: null }
		}

		let parsed = parseFloat(value);
		if (Number.isNaN(parsed)) {
			return { valid: false, error: `Invalid ${type} Price` }
		}
		else {
			return { valid: true, error: "", newValue: parseFloat(parsed.toFixed(2)) }
		}
	}

	function QtyValidation(value: any, type: string): { valid: boolean, error: string, newValue?: any } {

		//If empty, then we assume its fine
		if (value === undefined || value === null || value === 0) {
			return { valid: true, error: "", newValue: null }
		}

		let parsed = parseInt(value);
		if (Number.isNaN(parsed)) {
			return { valid: false, error: `Invalid ${type} Qty` }
		}
		else {
			return { valid: true, error: "", newValue: parsed }
		}
	}

	function DateValidation(value: any, type: string, fileType: FormatType = 'csv'): { valid: boolean, error: string, newValue?: any } {

		if (value === undefined || value === null) {
			return { valid: true, error: "", newValue: null }
		}

		const dateParts = value.toString().split(/\/| /); //Split by / and space
		if (dateParts.length > 2) { // dd/MM/yyyy at least
			let day = parseInt(dateParts[fileType === 'csv' ? 0 : 1]);
			let month = parseInt(dateParts[fileType === 'csv' ? 1 : 0]);
			let year = parseInt(dateParts[2]);

			if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
				return { valid: false, error: `Invalid ${type} Date Format` }
			}
			
			if (year < 100) year += 2000; //Only add if we have 2 digits
			const newDate = (day < 10 ? "0" + day : day) + "/" + (month < 10 ? "0" + month : month) + "/" + year;
			const newDatCheck = (month < 10 ? "0" + month : month) + "/" + (day < 10 ? "0" + day : day) + "/" + year;
			const date = new Date(newDatCheck);

			if (date.getDate() !== day || (date.getMonth() + 1) !== month || date.getFullYear() !== year){
				return { valid: false, error: "Invalid date format. Should be dd/MM/yyyy for csv or MM/dd/yyyy for excel import" };
			}

			return { valid: true, error: "", newValue: newDate };
		}

		return { valid: false, error: `Invalid ${type} Date Format` }
	}

	const importWizardExtraMatches: CustomColumns[] = [
		{
			title: "PrivatePrice",
			key: "PrivatePrice",
			required: true,
			display: false,
			defaultColumn: 1,
			validation: (value: any) => PriceValidation(value, "Private"),
		},
		{
			title: "PrivateQty",
			key: "PrivateQty",
			required: true,
			display: false,
			defaultColumn: 2,
			validation: (value: any) => QtyValidation(value, "Private"),
		},
		{
			title: "PrivateDateStart",
			key: "PrivateDateStart",
			required: true,
			display: false,
			defaultColumn: 3,
			validation: (value: any, formatType) => DateValidation(value, "Private", formatType),
		},
		{
			title: "PrivateDateEnd",
			key: "PrivateDateEnd",
			required: true,
			display: false,
			defaultColumn: 4,
			validation: (value: any, formatType) => DateValidation(value, "Private", formatType),
		},

		{
			title: "PbsPrice",
			key: "PbsPrice",
			required: true,
			display: false,
			defaultColumn: 5,
			validation: (value: any) => PriceValidation(value, "PBS"),
		},
		{
			title: "PbsQty",
			key: "PbsQty",
			required: true,
			display: false,
			defaultColumn: 6,
			validation: (value: any) => QtyValidation(value, "PBS"),
		},
		{
			title: "PbsDateStart",
			key: "PbsDateStart",
			required: true,
			display: false,
			defaultColumn: 7,
			validation: (value: any, formatType) => DateValidation(value, "Private", formatType),
		},
		{
			title: "PbsDateEnd",
			key: "PbsDateEnd",
			required: true,
			display: false,
			defaultColumn: 8,
			validation: (value: any, formatType) => DateValidation(value, "Private", formatType),
		},
		{
			title: "ClubPrice",
			key: "ClubPrice",
			required: true,
			display: false,
			defaultColumn: 9,
			validation: (value: any) => PriceValidation(value, "Club"),
		},
		{
			title: "ClubQty",
			key: "ClubQty",
			required: true,
			display: false,
			defaultColumn: 10,
			validation: (value: any) => QtyValidation(value, "Club"),
		},
		{
			title: "ClubDateStart",
			key: "ClubDateStart",
			required: true,
			display: false,
			defaultColumn: 11,
			validation: (value: any, formatType) => DateValidation(value, "Private", formatType),
		},
		{
			title: "ClubDateEnd",
			key: "ClubDateEnd",
			required: true,
			display: false,
			defaultColumn: 12,
			validation: (value: any, formatType) => DateValidation(value, "Private", formatType),
		},

	]
	
	const terminateWorkersRef = useRef<Function>();

	async function importStock(rows: any[]) {
		const webWorkerData: DispensePricingImportWorkerData = {
			data: rows
		};

		const errors: Map<string, string> = new Map<string, string>();
		let importedData: ProductsSpecialPricingExtended[] = [];

		const { runWorkers, terminateWorkers } = runBatchWorkers<DispensePricingImportWorkerData, any, DispensePricingImportWorkerResponseData>({
			workerData: webWorkerData,
			createWorker: () => new Worker(new URL('./dispensePricingImportWorker.ts', import.meta.url), { type: "module" }), 
			onWorkerResponse: (async (event, responseContext, httpErrors, reject) => {				
				if (!responseContext.response.data) {
					if (httpErrors) {
						const combinedError = httpErrors.join(", ");
						// Now using this combined error, construct a { stockName, error } object for each stock and push it onto the errors array
						responseContext.originalBatchData.forEach((r: any) => {
							const errorEntry = errors.get(r.__name);
							errors.set(r.__name ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
						});
					}
				} else {
					responseContext.response.data!.updatedStocks.forEach((stck: any, index) => {						
						responseContext.response.data!.updatedStocks[index].clubFixedPrice = isNaN(stck.clubFixedPrice) || stck.clubFixedPrice == null? undefined: stck.clubFixedPrice as number;
						responseContext.response.data!.updatedStocks[index].clubFixedPricePacks = isNaN(stck.clubFixedPricePacks) || stck.clubFixedPricePacks == null? undefined: stck.clubFixedPricePacks as number;
						responseContext.response.data!.updatedStocks[index].privateFixedPrice = isNaN(stck.privateFixedPrice) || stck.privateFixedPrice == null? undefined: stck.privateFixedPrice as number;
						responseContext.response.data!.updatedStocks[index].privateFixedPricePacks = isNaN(stck.privateFixedPricePacks) || stck.privateFixedPricePacks == null? undefined: stck.privateFixedPricePacks as number;
						responseContext.response.data!.updatedStocks[index].pbsFixedPrice = isNaN(stck.pbsFixedPrice) || stck.pbsFixedPrice == null? undefined: stck.pbsFixedPrice as number;
						responseContext.response.data!.updatedStocks[index].pbsFixedPricePacks = isNaN(stck.pbsFixedPricePacks) || stck.pbsFixedPricePacks == null? undefined: stck.pbsFixedPricePacks as number;
					});
					importedData = importedData.concat(responseContext.response.data!.updatedStocks)
				}

				setImportPercentage((prev) => Math.min(100, prev + ((1 / responseContext.numBatches) * 100)));
			}),
			onWorkersFinished: async(_) => {

				//We're starting with the full existing list of dispense pricing
				const ProductSpecialPricingQuantities: ProductsSpecialPricingExtendedWithError[] = dispensePricing.table.map((s) => ({...s}));

				for (const row of importedData) {

					const existingList: ProductsSpecialPricingExtendedWithError[] = [];
					const existingFromTable:ProductsSpecialPricingExtendedWithError[]  = ProductSpecialPricingQuantities.filter((s) => s.productID === row.productID);

					if (row.pbsFixedPricePacks != null && row.pbsFixedPricePacks > 0) {

						const identicalPbsQty = existingFromTable.find((s) => s.pbsFixedPricePacks === row.pbsFixedPricePacks);
						if (identicalPbsQty != null) {
							identicalPbsQty.pbsFixedPrice = row.pbsFixedPrice;
							identicalPbsQty.pbsFixedPriceStart = row.pbsFixedPriceStart;
							identicalPbsQty.pbsFixedPriceEnd = row.pbsFixedPriceEnd;
							existingList.push(identicalPbsQty);
							
							//Clear from this stk
							row.pbsFixedPricePacks = null;
							row.pbsFixedPrice = null;
							row.pbsFixedPriceStart = null;
							row.pbsFixedPriceEnd = null;
						}
					}

					if (row.clubFixedPricePacks != null && row.clubFixedPricePacks > 0) {
						const identicalClubQty = existingFromTable.find((s) => s.clubFixedPricePacks === row.clubFixedPricePacks);
						if (identicalClubQty != null) {
							identicalClubQty.clubFixedPrice = row.clubFixedPrice;
							identicalClubQty.clubFixedPriceStart = row.clubFixedPriceStart;
							identicalClubQty.clubFixedPriceEnd = row.clubFixedPriceEnd;
							existingList.push(identicalClubQty);

							//Clear from this stk
							row.clubFixedPricePacks = null;
							row.clubFixedPrice = null;
							row.clubFixedPriceStart = null;
							row.clubFixedPriceEnd = null;
						}
					}

					if (row.privateFixedPricePacks != null && row.privateFixedPricePacks > 0) {
						const identicalPrivateQty = existingFromTable.find((s) => s.privateFixedPricePacks === row.privateFixedPricePacks);
						if (identicalPrivateQty != null) {
							identicalPrivateQty.privateFixedPrice = row.privateFixedPrice;
							identicalPrivateQty.privateFixedPriceStart = row.privateFixedPriceStart;
							identicalPrivateQty.privateFixedPriceEnd = row.privateFixedPriceEnd;
							existingList.push(identicalPrivateQty);

							//Clear from this stk
							row.privateFixedPricePacks = null;
							row.privateFixedPrice = null;
							row.privateFixedPriceStart = null;
							row.privateFixedPriceEnd = null;
						}
					}

					//We've so far modified references to the existing list

					let added = false;

					if (
						row.pbsFixedPricePacks != null ||
						row.privateFixedPricePacks != null ||
						row.clubFixedPricePacks != null
					) {
						// Check if we can fit the new prices into an existing row
						const existingPrice = existingList.find(x =>
							((row.pbsFixedPricePacks == null && row.pbsFixedPrice == null) || x.pbsFixedPricePacks == null) &&
							((row.clubFixedPricePacks == null && row.clubFixedPrice == null) || x.clubFixedPricePacks == null) &&
							((row.privateFixedPricePacks == null && row.privateFixedPrice == null) || x.privateFixedPricePacks == null)
						);

						if (existingPrice) {
							if (row.pbsFixedPricePacks != null) {
								existingPrice.pbsFixedPricePacks = row.pbsFixedPricePacks;
								existingPrice.pbsFixedPrice = row.pbsFixedPrice;
								existingPrice.pbsFixedPriceStart = row.pbsFixedPriceStart;
								existingPrice.pbsFixedPriceEnd = row.pbsFixedPriceEnd;
								row.pbsFixedPricePacks = null;
								row.pbsFixedPrice = null;
								row.pbsFixedPriceStart = null;
								row.pbsFixedPriceEnd = null;
							}
							if (row.clubFixedPricePacks != null) {
								existingPrice.clubFixedPricePacks = row.clubFixedPricePacks;
								existingPrice.clubFixedPrice = row.clubFixedPrice;
								existingPrice.clubFixedPriceStart = row.clubFixedPriceStart;
								existingPrice.clubFixedPriceEnd = row.clubFixedPriceEnd;
								row.clubFixedPricePacks = null;
								row.clubFixedPrice = null;
								row.clubFixedPriceStart = null;
								row.clubFixedPriceEnd = null;
							}
							if (row.privateFixedPricePacks != null) {
								existingPrice.privateFixedPricePacks = row.privateFixedPricePacks;
								existingPrice.privateFixedPrice = row.privateFixedPrice;
								existingPrice.privateFixedPriceStart = row.privateFixedPriceStart;
								existingPrice.privateFixedPriceEnd = row.privateFixedPriceEnd;
								row.privateFixedPricePacks = null;
								row.privateFixedPrice = null;
								row.privateFixedPriceStart = null;
								row.privateFixedPriceEnd = null;
							}
						} else {
							added = true;
							ProductSpecialPricingQuantities.push(row);
						}
					}

					if (
						!added &&
						(row.privateFixedPrice != null ||
							row.pbsFixedPrice != null ||
							row.clubFixedPrice != null)
					) {
						// Any remaining special prices that don't have a QTY
						const existingPrice = existingList.find(
							(x) =>
								(row.privateFixedPrice == null || x.privateFixedPrice == null) &&
								(row.pbsFixedPrice == null || x.pbsFixedPrice == null) &&
								(row.clubFixedPrice == null || x.clubFixedPrice == null)
						);
						if (existingPrice) {
							if (row.pbsFixedPrice != null) {
								existingPrice.pbsFixedPrice = row.pbsFixedPrice;
								existingPrice.pbsFixedPriceStart = row.pbsFixedPriceStart;
								existingPrice.pbsFixedPriceEnd = row.pbsFixedPriceEnd;
								existingPrice.pbsFixedPricePacks = null;
							}
							if (row.clubFixedPrice != null) {
								existingPrice.clubFixedPrice = row.clubFixedPrice;
								existingPrice.clubFixedPriceStart = row.clubFixedPriceStart;
								existingPrice.clubFixedPriceEnd = row.clubFixedPriceEnd;
								existingPrice.clubFixedPricePacks = null;
							}
							if (row.privateFixedPrice != null) {
								existingPrice.privateFixedPrice = row.privateFixedPrice;
								existingPrice.privateFixedPriceStart = row.privateFixedPriceStart;
								existingPrice.privateFixedPriceEnd = row.privateFixedPriceEnd;
								existingPrice.privateFixedPricePacks = null;
							}
						} else {
							ProductSpecialPricingQuantities.push(row);
						}
					}
				}

				
				handleChange("table", ProductSpecialPricingQuantities);
				setImportPercentage(0); 
				setDispensePriceData(getEditableManagedCatalogueStockView(ProductSpecialPricingQuantities));
			}, 
			onWorkerError: (error, unprocessedData) => {
				unprocessedData.forEach(data => {
					const errorEntry = errors.get(data.__name);
					const errorMessage = error.message ?? "Unknown error occurred";
					errors.set(data.__name ?? "", errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
				})
			}
		});

		function matchingDataRulesShouldLeaveOut(stk: ProductsSpecialPricingExtended, newlyImportedStock: ProductsSpecialPricingExtended[]): boolean{
			let shouldILeaveOut = true;
			newlyImportedStock.forEach((possibleMatch: ProductsSpecialPricingExtended) => {
				if (stk.pbsFixedPricePacks != possibleMatch.pbsFixedPricePacks || stk.pbsFixedPricePacks == undefined)
					shouldILeaveOut = false;				

				if (shouldILeaveOut == false && stk.privateFixedPricePacks == possibleMatch.privateFixedPricePacks && stk.privateFixedPricePacks != undefined)
					shouldILeaveOut = true;

				if (shouldILeaveOut == false && stk.clubFixedPricePacks == possibleMatch.clubFixedPricePacks && stk.clubFixedPricePacks != undefined)
					shouldILeaveOut = true;
			});	
			return shouldILeaveOut;
		}

		terminateWorkersRef.current = terminateWorkers;

		await runWorkers;

		const returnErrors: { stockName: string, error: string }[] = [];

		errors.forEach((value: string, key: string) => {
			returnErrors.push({
				stockName: key,
				error: value,
			})
		});

		return [...returnErrors];
	}

	
	function datesOverlap(start1: Date | undefined, end1: Date | undefined, start2: Date | undefined, end2: Date | undefined): boolean {
		if (start1 == null || end1 == null || start2 == null || end2 == null) {
			return true; //If any date is null, we therefore assume it does overlap since theres no range!
		}
		
		if (start1 > end1 || start2 > end2) {
			return false; //Invalid date ranges
		}
		
		return (start1 <= end2 && start2 <= end1);
	}

	function parseDate(value: string | undefined | null): Date | undefined {
		if (value === undefined || value === null) {
			return undefined;
		}
		const date = new Date(value);
		return date;
	}

	function HasAnyErrors(row?: ProductsSpecialPricingExtendedWithError): boolean {
	if (row?.hasError) {
		for (const key in row.hasError) {
			if (row.hasError[key as keyof ProductsSpecialPricingExtended]) {
				return true;
			}
		}
	}
	return false;
	}
	
	///because the nameing isnt consistent through the code this method cant be reused as the prop names are diffrent.
	function validationMatchingDataRulesShouldLeaveOut(stk: CommonColumnProperties, newlyImportedStock: CommonColumnProperties[]): {
		result: boolean, message?: string } {
		for (let i = 0; i < newlyImportedStock.length; i++) {
			const possibleMatch = newlyImportedStock[i];
			const privateDatesOverlap = datesOverlap(parseDate(stk.PrivateDateStart), parseDate(stk.PrivateDateEnd), parseDate(possibleMatch.PrivateDateStart), parseDate(possibleMatch.PrivateDateEnd));
			const pbsDatesOverlap = datesOverlap(parseDate(stk.PbsDateStart), parseDate(stk.PbsDateEnd), parseDate(possibleMatch.PbsDateStart), parseDate(possibleMatch.PbsDateEnd));
			const clubDatesOverlap = datesOverlap(parseDate(stk.ClubDateStart), parseDate(stk.ClubDateEnd), parseDate(possibleMatch.ClubDateStart), parseDate(possibleMatch.ClubDateEnd));

			if (stk.PrivateQty === possibleMatch.PrivateQty &&
				((stk.PrivateDateStart === possibleMatch.PrivateDateStart && stk.PrivateDateEnd === possibleMatch.PrivateDateEnd) ||
				privateDatesOverlap) &&

				stk.PbsQty === possibleMatch.PbsQty &&
				((stk.PbsDateStart === possibleMatch.PbsDateStart && stk.PbsDateEnd === possibleMatch.PbsDateEnd) ||
				pbsDatesOverlap) &&

				stk.ClubQty === possibleMatch.ClubQty &&
				((stk.ClubDateStart === possibleMatch.ClubDateStart && stk.ClubDateEnd === possibleMatch.ClubDateEnd) ||
				clubDatesOverlap) 
			){
				return {result: true, message: ((privateDatesOverlap || pbsDatesOverlap || clubDatesOverlap) ? "dates cannot overlap" : undefined )}; 
			}
		}
		
		return { result: false }; //No matching data found
	}


	function WholeTableValidation(rows: CommonColumnProperties[]): CommonColumnProperties[] {
		let temp: CommonColumnProperties[] = [...rows];
		rows.forEach(row => {
			if (row.hasError === undefined || row.hasError === false) { //don"t bother validating bad rows
				var reasons:string[] = [];

				//Validate each section
				if (row.PrivatePrice !== null && row.PrivateQty !== null && (row.PrivatePrice <= 0 || row.PrivateQty <= 0)) reasons.push("Private price/quantity invalid");
				if (row.PbsPrice !== null && row.PbsQty !== null && (row.PbsPrice <= 0 || row.PbsQty <= 0)) reasons.push("PBS price/quantity invalid");
				if (row.ClubPrice !== null && row.ClubQty !== null && (row.ClubPrice <= 0 || row.ClubQty <= 0)) reasons.push("Club price/quantity invalid");
				
				if ((row.PrivatePrice === null && row.PrivateQty !== null) || (row.PrivatePrice !== null && row.PrivateQty === null)) reasons.push("Private price/quantity invalid");
                if ((row.PbsPrice === null && row.PbsQty !== null) || (row.PbsPrice !== null && row.PbsQty === null)) reasons.push("PBS price/quantity invalid");
                if ((row.ClubPrice === null && row.ClubQty !== null) || (row.ClubPrice !== null && row.ClubQty === null)) reasons.push("Club price/quantity invalid");

				if (row.PrivatePrice ===  null && row.PrivateQty === null && 
					row.PbsPrice === null && row.PbsQty === null && 
					row.ClubPrice === null && row.ClubQty === null) reasons.push("No prices or quantites are set");

				//Validate dates
				let privateStart = ParseDate(row.PrivateDateStart);
				let privateEnd = ParseDate(row.PrivateDateEnd);
				if (privateStart !== null && privateEnd !== null && 
					privateEnd < privateStart) reasons.push("Private end date is before start date");

				let pbsStart = ParseDate(row.PbsDateStart);
				let pbsEnd = ParseDate(row.PbsDateEnd);
				if (pbsStart !== null && pbsEnd !== null && 
					pbsEnd < pbsStart) reasons.push("PBS end date is before start date");

				let clubStart = ParseDate(row.ClubDateStart);
				let clubEnd = ParseDate(row.ClubDateEnd);
				if (clubStart !== null && clubEnd !== null && 
					clubEnd < clubStart) reasons.push("Club end date is before start date");
				
				row.hasError = reasons.length > 0;
				row.validationState = row.hasError ? ValidationState.Invalid : ValidationState.Valid;
				if (reasons.length > 0)
					row.message = reasons.join(", ");
			}
		});

		const lineMatch = (row1: CommonColumnProperties, row2: CommonColumnProperties): boolean => {
			if (row1 !== row2 &&
				row1.__apn === row2.__apn &&
				row1.__pde === row2.__pde &&
				row1.__supplier === row2.__supplier) {
					return true;
				}
			return false;
		}

		rows.filter(x => x.validationState ==  ValidationState.Valid).forEach((product) => {
			let similarProducts = rows.filter(mainList => lineMatch(mainList, product) && mainList.validationState == ValidationState.Valid);
			const validationLeaveOut = validationMatchingDataRulesShouldLeaveOut(product, similarProducts);
			if(similarProducts.length > 0 && validationLeaveOut.result){
				product.message = "Duplicate Line found. Qty must be different";
				if (validationLeaveOut.message)
					product.message += " and " + validationLeaveOut.message;
				product.validationState = ValidationState.Invalid;
				product.hasError = true;
			}
		})

		return temp;
	}	

	function getAgGridRows(): ProductsSpecialPricingExtended[] {
		const rows: ProductsSpecialPricingExtended[] = [];
		if(dispensePricingGridRef.current?.api){
			dispensePricingGridRef.current.api.forEachNode((node) => rows.push(node.data));
		}
		return rows;
	}

	const runningValidation = useRef(false);
	const pauseValidation = useRef(false);
	function agGridTableValidation() {

		let errorCount = 0;

		if (runningValidation.current)
			return;

		if (pauseValidation.current)
			return;

		runningValidation.current = true;

		console.log("agGridTableValidation")
		var hasChange = false;

		const privateKey = (row: ProductsSpecialPricingExtendedWithError) => "PRIVATE|" + row.productID + "|" + 
															 row.privateFixedPricePacks + "|";
		const pbsKey = (row: ProductsSpecialPricingExtendedWithError) => "PBS|" + row.productID + "|" + 
															 row.pbsFixedPricePacks + "|";
		const clubKey = (row: ProductsSpecialPricingExtendedWithError) => "CLUB|" + row.productID + "|" + 
															 row.clubFixedPricePacks + "|";

		const stocks: ProductsSpecialPricingExtendedWithError[] = getAgGridRows();

		const groupedStocks = stocks.reduce((acc, stock) => {
			var keys = [privateKey(stock), pbsKey(stock), clubKey(stock)];
			for (const key of keys) {
				if (!acc[key]) {
					acc[key] = [];
				}
				acc[key].push(stock);
			}
			return acc;
		}, {} as { [key: string]: ProductsSpecialPricingExtendedWithError[] });

		stocks.forEach((stock) => {
			hasChange = false;

			const duplicatePrivate = groupedStocks[privateKey(stock)]?.some((x) => x.productsSpecialPricingID !== stock.productsSpecialPricingID && x.privateFixedPrice != null) ?? false;
			const duplicatePBS = groupedStocks[pbsKey(stock)]?.some((x) => x.productsSpecialPricingID !== stock.productsSpecialPricingID && x.pbsFixedPrice != null) ?? false;
			const duplicateClub = groupedStocks[clubKey(stock)]?.some((x) => x.productsSpecialPricingID !== stock.productsSpecialPricingID && x.clubFixedPrice != null) ?? false;

			//Private has duplicate
			if (duplicatePrivate) {
				console.log(groupedStocks[privateKey(stock)]);
				//Check the dates overlap or not
				const privateDatesOverlap = groupedStocks[privateKey(stock)]?.filter(x => x.productsSpecialPricingID !== stock.productsSpecialPricingID).some((x) => datesOverlap(parseDate(stock.privateFixedPriceStart), parseDate(stock.privateFixedPriceEnd), parseDate(x.privateFixedPriceStart), parseDate(x.privateFixedPriceEnd))) ?? false;
				let isthisDuplicate = true;

				//They do, and we don't have an error set, so we set it
				if (privateDatesOverlap && (stock.hasError?.privateFixedPriceStart !== true || stock.hasError?.privateFixedPriceEnd !== true)) {
					stock.hasError = { ...stock.hasError, privateFixedPriceStart: true, privateFixedPriceEnd: true };
					hasChange = true;
				} else if (!privateDatesOverlap && (stock.hasError?.privateFixedPriceStart === true || stock.hasError?.privateFixedPriceEnd === true)) {
					//They don't overlap, but we have an error set, so we remove it
					stock.hasError = { ...stock.hasError, privateFixedPriceStart: false, privateFixedPriceEnd: false };
					hasChange = true;
					isthisDuplicate = false;
				} else if (!privateDatesOverlap) {
					isthisDuplicate = false;
				}

				if (isthisDuplicate && (stock.hasError?.privateFixedPrice !== true || stock.hasError?.privateFixedPricePacks !== true)) {
					stock.hasError = { ...stock.hasError, privateFixedPrice: true, privateFixedPricePacks: true };
					hasChange = true;
				} else if (!isthisDuplicate && (stock.hasError?.privateFixedPrice === true || stock.hasError?.privateFixedPricePacks === true)) {
					//They are not a duplicate, but we have an error set, so we remove it
					stock.hasError = { ...stock.hasError, privateFixedPrice: false, privateFixedPricePacks: false };
					hasChange = true;
				}
			} else if (stock.hasError?.privateFixedPrice === true || stock.hasError?.privateFixedPricePacks === true || stock.hasError?.privateFixedPriceStart === true || stock.hasError?.privateFixedPriceEnd === true) {
				stock.hasError = { ...stock.hasError, privateFixedPrice: false, privateFixedPricePacks: false, privateFixedPriceStart: false, privateFixedPriceEnd: false };
				hasChange = true;
			}

			if (duplicatePBS) {
				//Check the dates overlap or not
				const pbsDatesOverlap = groupedStocks[pbsKey(stock)]?.filter(x => x.productsSpecialPricingID !== stock.productsSpecialPricingID).some((x) => datesOverlap(parseDate(stock.pbsFixedPriceStart), parseDate(stock.pbsFixedPriceEnd), parseDate(x.pbsFixedPriceStart), parseDate(x.pbsFixedPriceEnd))) ?? false;
				let isthisDuplicate = true;

				//They do, and we don't have an error set, so we set it
				if (pbsDatesOverlap && (stock.hasError?.pbsFixedPriceStart !== true || stock.hasError?.pbsFixedPriceEnd !== true)) {
					stock.hasError = { ...stock.hasError, pbsFixedPriceStart: true, pbsFixedPriceEnd: true };
					hasChange = true;
				} else if (!pbsDatesOverlap && (stock.hasError?.pbsFixedPriceStart === true || stock.hasError?.pbsFixedPriceEnd === true)) {
					//They don't overlap, but we have an error set, so we remove it
					stock.hasError = { ...stock.hasError, pbsFixedPriceStart: false, pbsFixedPriceEnd: false };
					hasChange = true;
					isthisDuplicate = false;
				} else if (!pbsDatesOverlap) {
					isthisDuplicate = false;
				}

				if (isthisDuplicate && (stock.hasError?.pbsFixedPrice !== true || stock.hasError?.pbsFixedPricePacks !== true)) {
					stock.hasError = { ...stock.hasError, pbsFixedPrice: true, pbsFixedPricePacks: true };
					hasChange = true;
				} else if (!isthisDuplicate && (stock.hasError?.pbsFixedPrice === true || stock.hasError?.pbsFixedPricePacks === true)) {
					//They are not a duplicate, but we have an error set, so we remove it
					stock.hasError = { ...stock.hasError, pbsFixedPrice: false, pbsFixedPricePacks: false };
					hasChange = true;
				}
			} else if (stock.hasError?.pbsFixedPrice === true || stock.hasError?.pbsFixedPricePacks === true || stock.hasError?.pbsFixedPriceStart === true || stock.hasError?.pbsFixedPriceEnd === true) {
				stock.hasError = { ...stock.hasError, pbsFixedPrice: false, pbsFixedPricePacks: false, pbsFixedPriceStart: false, pbsFixedPriceEnd: false };
				hasChange = true;
			}

			if (duplicateClub) {
				//Check the dates overlap or not
				const clubDatesOverlap = groupedStocks[clubKey(stock)]?.filter(x => x.productsSpecialPricingID !== stock.productsSpecialPricingID).some((x) => datesOverlap(parseDate(stock.clubFixedPriceStart), parseDate(stock.clubFixedPriceEnd), parseDate(x.clubFixedPriceStart), parseDate(x.clubFixedPriceEnd))) ?? false;
				let isthisDuplicate = true;
				//They do, and we don't have an error set, so we set it
				if (clubDatesOverlap && (stock.hasError?.clubFixedPriceStart !== true || stock.hasError?.clubFixedPriceEnd !== true)) {
					stock.hasError = { ...stock.hasError, clubFixedPriceStart: true, clubFixedPriceEnd: true };
					hasChange = true;
				} else if (!clubDatesOverlap && (stock.hasError?.clubFixedPriceStart === true || stock.hasError?.clubFixedPriceEnd === true)) {
					//They don't overlap, but we have an error set, so we remove it
					stock.hasError = { ...stock.hasError, clubFixedPriceStart: false, clubFixedPriceEnd: false };
					hasChange = true;
					isthisDuplicate = false;
				} else if (!clubDatesOverlap) {
					isthisDuplicate = false;
				}
				if (isthisDuplicate && (stock.hasError?.clubFixedPrice !== true || stock.hasError?.clubFixedPricePacks !== true)) {
					stock.hasError = { ...stock.hasError, clubFixedPrice: true, clubFixedPricePacks: true };
					hasChange = true;
				} else if (!isthisDuplicate && (stock.hasError?.clubFixedPrice === true || stock.hasError?.clubFixedPricePacks === true)) {
					//They are not a duplicate, but we have an error set, so we remove it
					stock.hasError = { ...stock.hasError, clubFixedPrice: false, clubFixedPricePacks: false };
					hasChange = true;
				}
			} else if (stock.hasError?.clubFixedPrice === true || stock.hasError?.clubFixedPricePacks === true || stock.hasError?.clubFixedPriceStart === true || stock.hasError?.clubFixedPriceEnd === true) {
				stock.hasError = { ...stock.hasError, clubFixedPrice: false, clubFixedPricePacks: false, clubFixedPriceStart: false, clubFixedPriceEnd: false };
				hasChange = true;
			}

			if (hasChange) {
				const node = dispensePricingGridRef.current?.api.getRowNode(stock.productsSpecialPricingID ?? "");
				if (node) {
					node.setData({ ...stock});
				}
			}


			if (HasAnyErrors(stock)) {
				errorCount++;
			}
		});

		runningValidation.current = false;
		setNumberOfErrors(errorCount);
	}

	const tableRef = useRef<TableRef>(null);

	return (
		<>
			<Title pageTitle={isNewDispensePricing ? "New Special Pricing" : "Update Special Pricing"} crumbs={[{ name: "Special Pricing", path: "/app/dispense-pricing" }]} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col>
									<Row>
										<h4 className="mt-2">{dispensePricing.storeName}</h4>
									</Row>
									<Row>
										<Modal
											title="Confirm"
											show={showWarning}
											buttonType="primary"
											buttonText="Continue"
											buttonCloseText="Cancel"
											handleSubmit={handleSubmit}
											handleClose={() => setShowWarning(false)}
											description={
												<>
													<p>You are about to sync <b>Selected</b> special pricings down to the selected store(s).</p>
													<p>Are you sure you want to continue?</p>
												</>
											}
										/>
									</Row>
									<Row>
										<Col xs={12}>
											<ImportWizard
												title="Special Pricing"
												display={dispensePricing.importWizardPopup}
												handleClose={() => {
													if (terminateWorkersRef.current) terminateWorkersRef.current();
													handleChange("importWizardPopup", false);
												}}
												importAsync={(i) => importStock(i)}
												customColumns={importWizardExtraMatches}
												apnColumn={{
													defaultColumn: 13,
													display: false,
												}}
												pdeColumn={{
													defaultColumn: 14,
													display: false,
												}}
												supplierColumn={{
													defaultColumn: 15,
													display: false,
												}}
												matchAgainst="Products"
												validateTable={(rows) => WholeTableValidation(rows)}
												finish={() => handleChange("importWizardPopup", false)}
												importPercentage={importPercentage}
												matchedColumnOverride={{
													headerName: "Matched",
													field: "processed",
													pinned: true,
													valueGetter: (params) => params.data.message,
													sortable: true,
													maxWidth: 500,
													cellRendererSelector: (params) => {
														const issueArray = params.data.message.split(", ");
														const cellRendererParams = {
															decorator:
																params.data.processed === ProcessingState.Failed ? (
																	<ZTooltip
																		tooltipClassName="tooltip-text-start"
																		text={`This line has issues${issueArray.length > 0 ? ": \n• " + issueArray.join("\n• ") : ""}`}
																	>
																		<ZIcon iconClassName="me-1" icon={ZIconType.Alert} variant="warning" />
																	</ZTooltip>
																) : (
																	<ZTooltip text="This stock will be Imported">
																		<ZIcon iconClassName="me-1" icon={ZIconType.Upload} variant="primary" />
																	</ZTooltip>
																),
															decoratorPosition: "start",
															align: "left",
															children: (
																<div className="text-truncate overflow-hidden">{params.data.message}</div>
															),
														};
														return { component: "baseCellRenderer", params: cellRendererParams };
													},
												}}
											/>

											{/* <DispensePricingImport show={dispensePricing.importWizardPopup} handleClose={() => handleChange("importWizardPopup", false)} stores={newStores} /> */}
											<StockPopup show={dispensePricing.stockPopup} handleSave={(stockChanged: boolean) => {
												if (stockChanged) {
													console.log("Stock has been changed! Setting selected stocks to: ", [...dispensePricing.selectedStocks, dispensePricing.stockIndex].filter(onlyUnique));
													// If the stock has been changed, then we need to select this stock in the table
													tableRef.current?.selectRow(dispensePricing.stockIndex.toString());
												}
												handleChange("stockPopup", false);
											}} handleClose={() => handleChange("stockPopup", false)} />
											{/* <SystemStocksPopup show={dispensePricing.masterStockPopup} onClose={() => handleChange("masterStockPopup", false)} onSave={systemStocksPopupSave} canSelectAll={true} /> */}
											<AgGridToolbar
												search={true}
												ref={toolbarRef}
												className="pb-1"
												handleSubmit={(searchState, filterState) => {
													dispatch(addAGGridSelectedStocks([]));													
													dispensePricingGridRef.current?.api.deselectAll();
													dispensePricingGridRef.current?.api.setGridOption('includeHiddenColumnsInQuickFilter', true)
													dispensePricingGridRef.current?.api.setGridOption('quickFilterText', searchState?.keyword);
												}}                      
												searchProps={
													{placeholder: "by Name", hideContains: true, totalRows: dispensePriceData.length }
												}
												children={(
													<div className="d-inline-flex gap-1">
														{ 
														(dispensePricing.selectedStocks != undefined && dispensePricing.selectedStocks.length != 0) &&									
															<Z.Button variant="light" className="h-100" disabled={true} buttonText={dispensePricing.selectedStocks.length + " Selected"}/>
														}
														<ZTooltip text="Refresh">
															<Z.Button icon={ZIconType.Refresh} buttonSize="md" onClick={() => reLoadTable()} disabled={!user.permissions.priceGroups.canView} />
														</ZTooltip>
						
														<ZTooltip text={
															dispensePricing.selectedStocks.length == 0 ? `Please select a row to delete`:										  
															dispensePricing.selectedStocks.length > 0 ? `Delete ${dispensePricing.selectedStocks.length} rows` :
															dispensePricing.selectedStocks.length == dispensePricing.table.length ? `Delete all rows`:
																"Please select a row to delete"}>
															<Z.Button icon={ZIconType.Delete} buttonSize="md"  onClick={() => (user.permissions.managedStock.canDelete ? setShowDelete(true) : undefined)} 
																disabled={!user.permissions.promotions.canDelete || dispensePricing.selectedStocks.length < 1 } />
														</ZTooltip>
						
														<ZTooltip text="Import From Spreadsheet">
															<Z.Button
																size="sm"
																className="btn-icon custom-btn-icon"
																onClick={user.permissions.promotions.canImport ? () => handleChange("importWizardPopup", true) : undefined}
																disabled={!user.permissions.promotions.canCreate || !user.permissions.promotions.canUpdate} 
																icon={ZIconType.Upload}
																iconSize="18px"
																style={{height: 40}}
																/>
														</ZTooltip>
														{user.permissions.dispensePricing.canUpdate ?
															<CustomDropDown
																items={dispensePricing.stockGroupsDropdown}
																handleChecked={handleCheckedDropdown}
																displayDropdown={showMenu}
																setDisplayDropdown={setShowMenu}
																className="cutom-table-checkbox-dropdown d-inline-flex me-1"
																placeholder="Share Prices"
																selectedPlaceholder="Share Prices"
																
															/> : <></>}												
													</div>
												)}
											/>
											<AgGridReact<ProductsSpecialPricingExtendedWithError>
													ref={dispensePricingGridRef}                                    
													columnDefs={getDispensePricingConfig(user.permissions.dispensePricing.canUpdate)}
													rowData={dispensePriceData}
													pagination={true}
													paginationPageSize={20}
													className="ag-theme-quartz ag-theme-custom w-100"
													containerStyle={{ height: "60dvh" }}
													rowSelection="multiple"
													suppressRowClickSelection={true}
													suppressContextMenu
													singleClickEdit
													editType="fullRow"
													stopEditingWhenCellsLoseFocus={true}
													enterNavigatesVertically={true}
													enterNavigatesVerticallyAfterEdit={true}
													onSelectionChanged={(e) => {
														dispatch(addAGGridSelectedStocks(dispensePricingGridRef.current?.api.getSelectedRows().map((x) => x.productID)));
													}}
													onRowSelected={() => {
														dispatch(addAGGridSelectedStocks(dispensePricingGridRef.current?.api.getSelectedRows().map((x) => x.productID)));
													}}
													components={{
														baseCellRenderer: BaseCellRenderer,
														editablePriceCellRenderer: EditablePriceCellRenderer,
														editablePercentCellRenderer: EditablePercentCellRenderer,
														moneyCellEditor: MoneyCellEditor
													}}
													onCellKeyDown={(row) => {
				
													}}
													onCellEditingStopped={(event)=>{

													}}
													gridOptions={{
														rowClassRules: {
															"ag-row-error": (params) => {
																const anyHasError = Object.values(params.data?.hasError ?? {}).some((v) => v === true);
																return anyHasError;
															}
														},
													}}
													onRowDataUpdated={() => {
														agGridTableValidation();
													}}
													onRowValueChanged={() => {
														agGridTableValidation();
													}}

													getRowId={(p) => p.data.productsSpecialPricingID ?? ""}
													rowMultiSelectWithClick
													groupAllowUnbalanced
													animateRows			
												/>

										</Col>
									</Row>
									<Row className="mt-3">
										<Col sm={4}>
											<Link to="/app/dispense-pricing" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
												<i className="mdi mdi-arrow-left"></i> Back to All Special Pricing {" "}
											</Link>
										</Col>
										<Col sm={4} className="d-flex justify-content-center">
										{numberOfErrors > 0 && (
											<Alert className="mb-0 d-flex align-items-center" variant="danger">
												<ZIcon icon={ZIconType.Alert} iconClassName="pe-1"/>
												<div>
													There {numberOfErrors > 1 ? "are" : "is"} {numberOfErrors} {numberOfErrors > 1 ? "items" : "item"} with invalid data. 
													<br/>
													Please fix issues to allow syncing.
												</div>
											</Alert>
										)}
										</Col>
										<Col sm={4} className="d-flex flex-row-reverse">
											<ZTooltip text="You must select at least one line to save and sync" enabled={dispensePricing.selectedStocks.length === 0} className="flex-row">
												<Button variant="primary" className="btn cta-button" disabled={!user.permissions.dispensePricing.canUpdate || dispensePricing.loading || dispensePricing.selectedStocks.length === 0} onClick={() => setShowWarning(true)}>
													{dispensePricing.loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
													{dispensePricing.loading ? <>Saving...</> : <>Save</>}
												</Button>
											</ZTooltip>
										</Col>
									</Row>
									<Form onSubmit={handleSubmit} validated={isValidated} ref={form}>
									</Form>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
			<Modal
                title="Confirm Delete"
                show={showDelete}
                buttonType="primary"
                buttonText={"Continue"}
                buttonCloseText="Cancel"
                handleSubmit={() => {
                    handleDelete();
                    setShowDelete(false);
                    dispensePricingGridRef.current?.api.deselectAll();
                }}
                handleClose={() => setShowDelete(false)}
                description={
                    <>
                        <p>You are about to delete {dispensePricing.selectedStocks.length} selected {dispensePricing.selectedStocks.length > 1 ? "items": "item"}.</p>
                        <p>Are you sure you want to delete?</p>
                    </>
                }
            />
		</>
	);
}